import { registerBlockType } from "@wordpress/blocks"
import { __ } from "@wordpress/i18n"
import { useSelect } from "@wordpress/data"
import { useMemo, useState } from "@wordpress/element"
import {
  useBlockProps,
  InspectorControls,
  RichText,
} from "@wordpress/block-editor"
import {
  PanelBody,
  TextControl,
  CheckboxControl,
  Spinner,
} from "@wordpress/components"

const Edit = ({ attributes, setAttributes }) => {
  const blockProps = useBlockProps()
  const { mainTitle, description, resourcesTitle, whitePapers = [] } = attributes

  const [searchTerm, setSearchTerm] = useState("")

  // Fetch all WhitePaper posts
  const whitePaperPosts = useSelect((select) => {
    return select("core").getEntityRecords("postType", "whitepaper", {
      per_page: -1,
      _fields: ["id", "title"],
      status: "publish",
    })
  }, [])

  const isLoading = whitePaperPosts === null

  // Build options once
  const whitePaperOptions = useMemo(() => {
    if (!whitePaperPosts) return []
    return whitePaperPosts.map((post) => ({
      value: String(post.id),
      label: post.title?.rendered || `#${post.id}`,
    }))
  }, [whitePaperPosts])

  // Filter by search
  const filteredOptions = useMemo(() => {
    const q = searchTerm.trim().toLowerCase()
    if (!q) return whitePaperOptions
    return whitePaperOptions.filter((opt) => opt.label.toLowerCase().includes(q))
  }, [whitePaperOptions, searchTerm])

  const MAX = 3
  const toggleSelect = (value) => {
    const has = whitePapers.includes(value)
    if (has) {
      setAttributes({ whitePapers: whitePapers.filter((v) => v !== value) })
    } else if (whitePapers.length < MAX) {
      setAttributes({ whitePapers: [...whitePapers, value] })
    }
  }

  return (
    <>
      <div {...blockProps}>
        <InspectorControls>
          <PanelBody title={__("Settings", "puroclean")}>
            <TextControl
              label={__("Main Title", "puroclean")}
              value={mainTitle}
              onChange={(value) => setAttributes({ mainTitle: value })}
            />
            <TextControl
              label={__("Description", "puroclean")}
              value={description}
              onChange={(value) => setAttributes({ description: value })}
            />
            <TextControl
              label={__("Resources Title", "puroclean")}
              value={resourcesTitle}
              onChange={(value) => setAttributes({ resourcesTitle: value })}
            />
            {/* ---- New: Search + Checkbox multiselect ---- */}
            <div style={{ marginTop: 12 }}>
              <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>
                {__("Select White Papers", "puroclean")} ({whitePapers.length}/{MAX})
              </label>

              <TextControl
                placeholder={__("Search…", "puroclean")}
                value={searchTerm}
                onChange={setSearchTerm}
              />

              {isLoading && <Spinner />}

              {!isLoading && (
                <div
                  style={{
                    maxHeight: 240,
                    overflow: "auto",
                    border: "1px solid #e2e2e2",
                    borderRadius: 4,
                    padding: 8,
                    marginTop: 8,
                  }}
                >
                  {filteredOptions.length === 0 && (
                    <p style={{ margin: 0, color: "#666" }}>{__("No results", "puroclean")}</p>
                  )}

                  {filteredOptions.map((opt) => {
                    const checked = whitePapers.includes(opt.value)
                    const disableNew = !checked && whitePapers.length >= MAX
                    return (
                      <CheckboxControl
                        key={opt.value}
                        label={opt.label}
                        checked={checked}
                        onChange={() => toggleSelect(opt.value)}
                        disabled={disableNew}
                      />
                    )
                  })}
                </div>
              )}

              <p style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
                {__("Select up to 3 white papers to display", "puroclean")}
              </p>
            </div>
            {/* ------------------------------------------- */}
          </PanelBody>
        </InspectorControls>

        <div className="mt-8 md:mt-0 py-8 md:py-16 bg-white shadow-[0px_0px_60px_#0000000F]">
          <div className="max-w-inner mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 md:gap-24">
              <div className="lg:col-span-2">
                <RichText
                  tagName="h2"
                  className="~text-lg/4xl font-bold mb-4 leading-tight"
                  value={mainTitle}
                  onChange={(value) => setAttributes({ mainTitle: value })}
                />
                <RichText
                  tagName="p"
                  className="text-lg"
                  value={description}
                  onChange={(value) => setAttributes({ description: value })}
                />
              </div>
              
              <div className="lg:col-span-3">
                <RichText
                  tagName="h3"
                  className="~text-lg/2xl text-primary font-bold mb-4 leading-tight"
                  value={resourcesTitle}
                  onChange={(value) => setAttributes({ resourcesTitle: value })}
                />
                
                <div className="md:space-y-6">
                  <div className="md:space-y-2">
                    {whitePaperPosts && whitePapers.map((id) => {
                      const post = whitePaperPosts.find((p) => String(p.id) === id)
                      if (!post) return null
                      return (
                        <div key={id} className="py-2">
                          <span className="inline-flex items-center text-primary font-semibold text-lg leading-tight">
                            {post.title.rendered}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

registerBlockType("puroclean/learn-more", {
  edit: Edit,
  save: () => null // Server-side rendered block
}) 